import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  signUp,
  confirmSignUp,
  resendConfirmationCode,
  signIn,
  PORTAL_ROUTE_BY_GROUP,
} from '../auth/cognito';
import './Login.css';

const STEP_REGISTER = 'register';
const STEP_VERIFY   = 'verify';

export default function Signup() {
  const navigate = useNavigate();

  const [step, setStep]               = useState(STEP_REGISTER);
  const [firstName, setFirstName]     = useState('');
  const [lastName, setLastName]       = useState('');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [code, setCode]               = useState('');
  const [error, setError]             = useState(null);
  const [busy, setBusy]               = useState(false);
  const [resent, setResent]           = useState(false);

  async function onRegister(e) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPwd) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await signUp(firstName.trim(), lastName.trim(), email.trim(), password);
      setStep(STEP_VERIFY);
    } catch (err) {
      setError(err.message || 'Sign up failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmSignUp(email.trim(), code.trim());
      // Auto sign-in after confirmation so the user lands straight in their portal
      const user = await signIn(email.trim(), password);
      navigate(PORTAL_ROUTE_BY_GROUP[user.group] || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError(null);
    setResent(false);
    try {
      await resendConfirmationCode(email.trim());
      setResent(true);
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    }
  }

  if (step === STEP_VERIFY) {
    return (
      <section className="login-page">
        <div className="login-card">
          <h1 className="login-title">Verify your email</h1>
          <p className="login-sub">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below to
            activate your account.
          </p>

          <form onSubmit={onVerify} className="login-form">
            <label className="login-field">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </label>

            {error && <p className="login-error">{error}</p>}
            {resent && <p className="login-success">Code resent. Check your inbox.</p>}

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify & sign in'}
            </button>
          </form>

          <p className="login-foot">
            Didn't receive a code?{' '}
            <button className="link-btn" onClick={onResend}>Resend</button>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="login-page">
      <div className="login-card">
        <h1 className="login-title">Create an account</h1>
        <p className="login-sub">Free to join. Book sessions and track your child's progress.</p>

        <form onSubmit={onRegister} className="login-form">
          <div className="login-field-row">
            <label className="login-field">
              <span>First name</span>
              <input
                type="text"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </label>
            <label className="login-field">
              <span>Last name</span>
              <input
                type="text"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </label>
          </div>

          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="login-hint">Min. 10 characters, must include upper &amp; lowercase and a number.</span>
          </label>

          <label className="login-field">
            <span>Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
            />
          </label>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="login-foot">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </section>
  );
}
